import { Stack, Typography, Box } from '@mui/material'
import React from 'react'
import RcmPrimaryButton from '../../../shared/components/buttons/RcmPrimaryButton'
import HavingTrouble from '../../login/shared/components/HavingTrouble'
import PasswordDesktopLockIcon from '../../../shared/icons/PasswordDesktopLockIcon'
import useDocumentTitle from '../../../shared/hooks/useDocumentTitle'
import { LOGIN_REDIRECT_ERROR_MESSAGE } from '../../login/constants'
import { msal } from '../../../system/environment/Config.json'
import { Logger } from '../../../system/appinsights/Logger'
import { useMsal } from '@azure/msal-react'
//TODO: Uncomment this once login_hint is fixed [1]
//import { RegistrationContext } from '../RegistrationContext'
import {
  RegistrationIDPInterstitialPendingKey,
  RegistrationIDPInterstitialProviderKey
} from '../../../shared/Constants'
import { setSessionStorage } from '../../../shared/utilities/Session'

export type IDPProviderType = 'Google' | 'Microsoft' | 'Apple' | 'Default'

interface IIDPProviderMessages {
  loginWith: string
  loginUsing: string
  rememberUsing: string
}

const IDP_MESSAGES: Record<IDPProviderType, IIDPProviderMessages> = {
  Google: {
    loginWith: 'your Google Credentials',
    loginUsing: 'your Google Credentials',
    rememberUsing: 'your Google account'
  },
  Microsoft: {
    loginWith: 'your Microsoft Credentials',
    loginUsing: 'your Microsoft Credentials',
    rememberUsing: 'your Microsoft account'
  },
  Apple: {
    loginWith: 'your Apple Credentials',
    loginUsing: 'your Apple Credentials',
    rememberUsing: 'your Apple account'
  },
  Default: {
    loginWith: 'your email address and password',
    loginUsing: 'your personal email',
    rememberUsing: 'your personal email'
  }
}

const resolveMessages = (provider?: string): IIDPProviderMessages => {
  const key = provider
    ? ((provider.charAt(0).toUpperCase() + provider.slice(1).toLowerCase()) as IDPProviderType)
    : 'Default'
  return IDP_MESSAGES[key] ?? IDP_MESSAGES.Default
}

const shouldShowPostRedirectInterstitial = (provider?: string) => {
  const normalizedProvider = provider?.toLowerCase()
  return normalizedProvider === 'microsoft' || normalizedProvider === 'google'
}

interface IRegistrationIDPContinueScreenProps {
  provider?: string
  onContinue?: () => void
}

const RegistrationIDPContinueScreen: React.FC<IRegistrationIDPContinueScreenProps> = ({ provider, onContinue }) => {
  useDocumentTitle({ title: 'Registration Complete' })

  const { instance: msalInstance } = useMsal()
  //TODO: [1]
  //const { email } = useContext(RegistrationContext)

  const messages = resolveMessages(provider)

  const handleContinueClick = () => {
    if (onContinue) {
      onContinue()
      return
    }

    if (shouldShowPostRedirectInterstitial(provider)) {
      setSessionStorage(RegistrationIDPInterstitialPendingKey, 'true')
      setSessionStorage(RegistrationIDPInterstitialProviderKey, provider ?? 'Default')
    }

    msalInstance
      .loginRedirect({
        //TODO: [1]
        //login_hint: email,
        scopes: ['User.Read'],
        prompt: 'login',
        domainHint: encodeURI(msal.domainHint)
      })
      .catch(() => {
        Logger.LogException({ message: LOGIN_REDIRECT_ERROR_MESSAGE, exceptionType: 'MSAL' })
      })
  }

  return (
    <Stack
      spacing={5}
      justifyContent="center"
      alignItems="center"
      sx={{ maxWidth: '31.25rem', px: { xs: 2, sm: 0, md: 4 } }}
      mt={{ xs: 4, sm: 8 }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        <PasswordDesktopLockIcon
          sx={{
            fontSize: 60,
            stroke: (theme) => theme.palette.colors?.blue2
          }}
        />
      </Box>

      <Typography variant="h1" sx={{ fontSize: { xs: '1.25rem', sm: '1.625rem' }, textAlign: 'center' }}>
        Rockefeller Digital
      </Typography>

      <Stack spacing={2} sx={{ width: '100%' }}>
        <Typography variant="body1" sx={{ fontWeight: 700, color: 'text.primary', textAlign: 'left' }}>
          You can now access Rockefeller Digital logging in with {messages.loginWith}.
        </Typography>

        <Typography variant="body1" sx={{ color: 'text.primary', textAlign: 'left' }}>
          When you visit Rockefeller Digital online or in the mobile app, you will be prompted to login using{' '}
          {messages.loginUsing}.
        </Typography>

        <Typography
          variant="body1"
          sx={{
            color: 'text.primary',
            textAlign: 'left'
          }}
        >
          Remember, this simply means you are using {messages.rememberUsing} to securely log in, but Rockefeller Digital
          does not have access to your email or other personal information.
        </Typography>
      </Stack>

      <RcmPrimaryButton
        fullWidth
        onClick={handleContinueClick}
        sx={{
          maxWidth: '250px',
          paddingTop: 1.5,
          paddingBottom: 1.5,
          bgcolor: '#0E687B',
          '&:hover': {
            bgcolor: '#0A4F5E'
          }
        }}
      >
        Continue
      </RcmPrimaryButton>

      <Box mt={1}>
        <HavingTrouble />
      </Box>
    </Stack>
  )
}

export default RegistrationIDPContinueScreen
